/**
 * P2.2 — tune detail. One page per tune carrying >=2 hymns (~124 routes).
 *
 * Single-hymn tunes get no page by design (plan D4): 446 of the 570 tunes appear
 * once, and a page per singleton is thin content. Nothing is hidden — the hymn page
 * still names its tune.
 */
import { useParams } from 'react-router-dom'

import { HYMNAL_1982, index } from '@/data/hymnal'
import { Crumb, HymnList, SectionTitle } from '@/components/HymnList'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'

export default function TunePage() {
  const { slug } = useParams<{ slug: string }>()
  const tune = slug ? index.tuneBySlug.get(slug) : undefined

  if (!tune) {
    return (
      <NotFound
        title="No such tune page"
        detail="Tunes used by only one hymn have no page of their own — look it up from the hymn instead."
        backTo={`/hymnal/${HYMNAL_1982.id}`}
        backLabel="Browse The Hymnal 1982"
      />
    )
  }

  const facts = [
    ['Meter', tune.meter],
    ['Key', tune.key],
    ['Composer', tune.composer],
    ['Arranger', tune.arranger],
    ['Harmonizer', tune.harmonizer],
  ].filter(([, v]) => Boolean(v)) as [string, string][]

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title={tune.name}
        description={`The tune ${tune.name}${tune.meter ? ` (${tune.meter})` : ''} in The Hymnal 1982, sung to ${tune.hymns.length} hymns.`}
        path={`/hymnal/${HYMNAL_1982.id}/tune/${tune.slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'MusicComposition',
          name: tune.name,
          ...(tune.composer ? { composer: { '@type': 'Person', name: tune.composer } } : {}),
        }}
      />
      <Crumb label={tune.name} />

      <header className="mb-8">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Tune</p>
        <h1 className="text-3xl sm:text-4xl font-display tracking-wide text-foreground">{tune.name}</h1>
      </header>

      {facts.length > 0 && (
        <dl className="mb-8">
          {facts.map(([label, value]) => (
            <div
              key={label}
              className="grid grid-cols-[7.5rem_1fr] gap-x-4 py-1.5 border-b border-border last:border-0"
            >
              <dt className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5">{label}</dt>
              <dd className="text-sm text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      <section>
        <SectionTitle>
          Sung to {tune.hymns.length} {tune.hymns.length === 1 ? 'hymn' : 'hymns'}
        </SectionTitle>
        <HymnList hymns={tune.hymns} />
      </section>
    </article>
  )
}
